import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, MessageCircle, Zap, Shield, HeadphonesIcon, CheckCircle2, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";

const WHY_US = [
  { icon: Zap, title: "2-hour reply", desc: "We respond to every inquiry within 2 hours during business hours." },
  { icon: Shield, title: "No commitment", desc: "Free consultation, no pressure. Explore what fits your brand." },
  { icon: HeadphonesIcon, title: "Dedicated support", desc: "A senior account manager stays with you from brief to delivery." },
  { icon: MessageCircle, title: "WhatsApp ready", desc: "Prefer to chat? Reach us instantly on WhatsApp anytime." },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "Consultation", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/leads", { ...form, source: "contact" });
      toast.success("Got it! We'll be in touch within 2 hours.");
      setForm({ name: "", email: "", phone: "", subject: "Consultation", message: "" });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Failed to send");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="contact-page" className="pt-24">

      {/* HERO */}
      <section className="container mx-auto px-4 pt-16 pb-24 grid lg:grid-cols-2 gap-12">
        <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF007F] mb-3">Contact</div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-none mb-6">
            Let's <span className="text-gradient-animate">talk.</span>
          </h1>
          <p className="text-lg text-[#52525B] mb-10 max-w-md">
            Tell us about your brand, your goals, or your shoot. We'll reply within 2 hours during business hours.
          </p>

          <div className="space-y-4 mb-10">
            <div className="flex items-start gap-3"><Phone className="w-5 h-5 text-[#FF007F] mt-0.5" />
              <div><div className="text-xs uppercase tracking-widest text-zinc-500">Phone</div><a href="tel:9566115611" className="font-display font-bold text-lg hover:text-[#FF007F] transition-colors">9566115611</a></div></div>
            <div className="flex items-start gap-3"><Mail className="w-5 h-5 text-[#FF007F] mt-0.5" />
              <div><div className="text-xs uppercase tracking-widest text-zinc-500">Email</div><a href="mailto:info@cftcreations.com" className="font-display font-bold text-lg hover:text-[#FF007F] transition-colors">info@cftcreations.com</a></div></div>
            <div className="flex items-start gap-3"><MapPin className="w-5 h-5 text-[#FF007F] mt-0.5" />
              <div><div className="text-xs uppercase tracking-widest text-zinc-500">Studio</div><div className="font-display font-bold text-lg">D.No 31, Arappalayam Cross Road,<br/>Madurai, Tamil Nadu 625016</div></div></div>
            <div className="flex items-start gap-3"><Clock className="w-5 h-5 text-[#FF007F] mt-0.5" />
              <div><div className="text-xs uppercase tracking-widest text-zinc-500">Hours</div><div className="font-display font-bold text-lg">Mon–Sat · 9am–8pm</div></div></div>
          </div>

          <iframe title="map" className="w-full h-64 rounded-2xl border border-zinc-200"
            src="https://www.openstreetmap.org/export/embed.html?bbox=80.19%2C13.07%2C80.23%2C13.10&layer=mapnik&marker=13.085%2C80.21" />
        </motion.div>

        <motion.form onSubmit={submit} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
          className="bg-white border border-zinc-200 rounded-2xl p-8 h-fit space-y-4 shadow-sm">
          <h2 className="font-display font-extrabold text-2xl mb-2">Send a message</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="contact-name" className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:outline-none focus:border-[#FF007F] transition-colors" />
            <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="contact-email" className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:outline-none focus:border-[#FF007F] transition-colors" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="contact-phone" className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:outline-none focus:border-[#FF007F] transition-colors" />
            <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:outline-none focus:border-[#FF007F] transition-colors">
              {["Consultation", "Branding", "Digital Marketing", "Production", "Studio Booking", "Props Rental", "Web Development", "Other"].map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </div>
          <textarea required rows={5} placeholder="Tell us about your project…" value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            data-testid="contact-message" className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:outline-none focus:border-[#FF007F] transition-colors" />
          <button type="submit" disabled={busy} data-testid="contact-submit"
            className="group w-full px-6 py-3 rounded-full bg-gradient-brand-animate text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50
              transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_28px_rgba(255,0,127,0.4)]">
            {busy ? "Sending…" : <>Send Message <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" /></>}
          </button>
          <p className="text-xs text-zinc-400 text-center">We respond within 2 hours · No spam, ever</p>
        </motion.form>
      </section>

      {/* WHY REACH OUT */}
      <section className="bg-zinc-50 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF007F] mb-3">Why CFT</div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">What to expect when you reach out</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY_US.map((w, idx) => (
              <motion.div key={w.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                whileHover={{ y: -5, boxShadow: "0 10px 28px rgba(255,0,127,0.1)" }}
                className="bg-white rounded-2xl p-6 border border-zinc-200 cursor-default">
                <div className="w-11 h-11 rounded-xl bg-gradient-brand grid place-items-center mb-4">
                  <w.icon className="w-5 h-5 text-white" />
                </div>
                <h4 className="font-display font-bold text-lg mb-2">{w.title}</h4>
                <p className="text-sm text-zinc-500 leading-relaxed">{w.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="py-16 container mx-auto px-4">
        <div className="bg-[#0A1128] rounded-2xl p-10 flex flex-col md:flex-row items-center justify-between gap-6 text-white">
          <div>
            <h3 className="font-display font-black text-2xl sm:text-3xl mb-1">Prefer a quick chat on WhatsApp?</h3>
            <p className="text-white/60 text-sm">Our team is active Mon–Sat, 9am–8pm. No bots, real humans.</p>
          </div>
          <a href="https://wa.me/919566115611" target="_blank" rel="noreferrer"
            className="shrink-0 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#25D366] text-white font-bold hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(37,211,102,0.4)] transition-all duration-300">
            <MessageCircle className="w-5 h-5" /> Chat on WhatsApp
          </a>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-zinc-50">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-12">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF007F] mb-3">FAQ</div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">Common questions</h2>
          </div>
          <div className="space-y-4">
            {[
              { q: "How quickly can you start on a project?", a: "Most projects kick off within 3–5 business days of signing. For urgent shoots or campaigns, we can often start within 24–48 hours." },
              { q: "Do you work with businesses outside Chennai / Madurai?", a: "Absolutely. While our studios are in Tamil Nadu, we handle digital marketing, branding, and web projects for clients across India and globally." },
              { q: "What's the minimum budget to work with CFT?", a: "We work with a wide range of budgets. A single studio session starts at ₹1,500/hr, while brand packages typically begin at ₹25,000. Book a free consultation to get a tailored quote." },
              { q: "Can I visit the studio before booking?", a: "Yes! We welcome walk-ins Mon–Sat, 10am–6pm or you can schedule a studio tour through this contact form." },
              { q: "Do you offer monthly retainers?", a: "Yes — we offer Social Media, Digital Marketing, and Brand Management on monthly retainers with dedicated account managers and transparent reporting." },
            ].map((faq, idx) => (
              <motion.details key={idx}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.08 }}
                className="group bg-white border border-zinc-200 rounded-2xl px-6 py-5 cursor-pointer open:shadow-md transition-shadow">
                <summary className="font-display font-bold text-lg list-none flex items-center justify-between gap-4 select-none">
                  {faq.q}
                  <span className="w-6 h-6 rounded-full bg-zinc-100 group-open:bg-[#FF007F] grid place-items-center shrink-0 transition-colors">
                    <ArrowRight className="w-3 h-3 text-zinc-500 group-open:text-white group-open:rotate-90 transition-all" />
                  </span>
                </summary>
                <p className="mt-3 text-sm text-zinc-500 leading-relaxed">{faq.a}</p>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* QUICK LINKS */}
      <section className="py-20 container mx-auto px-4">
        <div className="text-center mb-10">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF007F] mb-3">Explore</div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">Not sure where to start?</h2>
          <p className="text-zinc-500 mt-3 max-w-lg mx-auto">Browse our services, see our work, or generate an instant AI quote.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { label: "Our Services", desc: "Branding, ads, production & more", to: "/services", color: "#FF007F" },
            { label: "Portfolio", desc: "Work we're proud of", to: "/portfolio", color: "#FF7F00" },
            { label: "Studio Rental", desc: "Book a pro studio by the hour", to: "/studios", color: "#00BFE0" },
            { label: "AI Quote", desc: "Get a tailored proposal in 30s", to: "/quote", color: "#9B00FF" },
          ].map((item, idx) => (
            <motion.div key={item.to}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.1 }}
              whileHover={{ y: -5 }}>
              <Link to={item.to}
                className="group flex flex-col h-full border border-zinc-200 rounded-2xl p-6 bg-white hover:border-transparent hover:shadow-lg transition-all duration-300">
                <div className="w-2 h-2 rounded-full mb-4" style={{ background: item.color }} />
                <h4 className="font-display font-bold text-lg mb-1 group-hover:text-[#FF007F] transition-colors">{item.label}</h4>
                <p className="text-sm text-zinc-500 flex-1">{item.desc}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold" style={{ color: item.color }}>
                  Explore <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

    </div>
  );
}
